/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useContext, useRef } from 'react'

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import { setupAxiosInterceptors } from 'services/interceptor'

// @mui material components
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import Icon from '@mui/material/Icon'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'

// Material Dashboard 2 PRO React examples
import Sidenav from 'examples/Sidenav'
import Configurator from 'examples/Configurator'

// Material Dashboard 2 PRO React themes
import theme from 'assets/theme'
import themeRTL from 'assets/theme/theme-rtl'

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from 'assets/theme-dark'
import themeDarkRTL from 'assets/theme-dark/theme-rtl'

// RTL plugins
import rtlPlugin from 'stylis-plugin-rtl'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'

// Material Dashboard 2 PRO React routes
import routes from 'routes'
import crudRoutes from 'routes/crud'

// Material Dashboard 2 PRO React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  AuthContext,
  setDarkMode,
} from 'context'

import { OnboardingProvider } from 'context/OnboardingContext'

// Onboarding Pages
import LandingPage from 'pages/onboarding/LandingPage'
import EntityReview from 'pages/onboarding/EntityReview'
import AddressReview from 'pages/onboarding/AddressReview'
import ContactReview from 'pages/onboarding/ContactReview'
import UserReview from 'pages/onboarding/UserReview'
import SubscriptionPreview from 'pages/onboarding/SubscriptionPreview'
import BillingPage from 'pages/onboarding/BillingPage'
import FinalAttestation from 'pages/onboarding/FinalAttestation'
import SuccessPage from 'pages/onboarding/SuccessPage'
import CustomerContinuation from 'pages/onboarding/CustomerContinuation'

import { getPermissions } from 'config/Permissions'

// Images
// import brandWhite from 'assets/images/logo-ct.png'
import brandWhite from 'assets/images/logos/svg/White logo - no background.svg'
// import brandDark from 'assets/images/logo-ct-dark.png'
import brandDark from 'assets/images/logos/svg/Black logo - no background.svg'
import ProtectedRoute from 'examples/ProtectedRoute'
import Login from 'auth/login'
import ForgotPassword from 'auth/forgot-password'
import ResetPassword from 'auth/reset-password'
import SetupLink from 'auth/setup-link'
import AuthService from 'services/auth-service'
import { Helmet } from 'react-helmet'
import EmailVerification from 'auth/verify-email'
import VerifyEmailPage from 'auth/verify-email/verify'
import Summary from 'cruds/device-management/internal-device-management/summary'
import ServiceRequestDetail from 'cruds/device-management/internal-device-management/view-device/service-request-detail'
import Loading from 'examples/Loading'
import Dashboard from 'cruds/dashboard'
import { ToastContainer, toast } from 'react-toastify';
import HtmlTemplate from 'template'
import PublicViewDevice from 'cruds/device-management/internal-device-management/public-view-device'
import NotFound from 'template/NotFound'
import { setLayout } from 'context'
import ClaimLandingRouter from 'pages/onboarding/ClaimLandingRouter'
import InstallFlowPage from 'pages/onboarding/install/InstallFlowPage'

export default function App({ ability }) {
  const [controller, dispatch] = useMaterialUIController()
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller
  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const [rtlCache, setRtlCache] = useState(null)
  const { pathname } = useLocation()

  const authContext = useContext(AuthContext)
  const [userDetails, setUserDetails] = useState({
    name: '',
    image: '',
    role: '',
  })

  const [isDemo, setIsDemo] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const routesRef = useRef(routes)

  useEffect(() => {
    setIsDemo(process.env.REACT_APP_IS_DEMO === 'true')
  }, [])

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate()
  setupAxiosInterceptors(() => {
    authContext.logout();
    navigate("/auth/login");
    toast.error("Session expired. Please login again.");
  });

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin],
    })

    setRtlCache(cacheRtl)
  }, [])

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false)
      setOnMouseEnter(true)
    }
  }

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true)
      setOnMouseEnter(false)
    }
  }

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator)

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction)
  }, [direction])

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  useEffect(() => {
    if (pathname.startsWith('/onboard') && !pathname.includes('onboarding')) {
      setDarkMode(dispatch, false)
      setLayout(dispatch, 'page');
    }
  }, [pathname, setLayout, layout]);

  useEffect(() => {
    if (!authContext.isAuthenticated) {
      loadingClose()
      return
    }
    ; (async () => {
      setIsLoading(true)
      const id = await authContext.getCurrentUser()
      const response = await AuthService.getProfile()
      setUserDetails({
        name: response.data.attributes.name,
        image: response.data.attributes.profile_image,
        role: response.included[0].id,
      })
      // authContext.setRoleId(response.included[0].id)
      if (response.included[0].id == 6) {
        authContext.getAccessList()
      }
      if (response.included[0].id == 7) {
        authContext.getAccessList()
      }
      localStorage.setItem('role', response.included[0].attributes.name)
      setDarkMode(dispatch, response.data.attributes.theme)
      const { rules, newRoutes } = await getPermissions(id, routes)
      routesRef.current = newRoutes
      ability.update(rules)
      loadingClose()
    })()
  }, [authContext.isAuthenticated])

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.key === 'user-name') {
        route.name = userDetails.name
      }
      if (route.collapse) {
        return getRoutes(route.collapse)
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        )
      }

      return null
    })

  const getCrudRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getCrudRoutes(route.collapse)
      }
      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        )
      }

      return null
    })

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: 'pointer' }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  )

  const loadingClose = () => {
    let timeout = setTimeout(() => {
      setIsLoading(false)
      clearTimeout(timeout)
    }, 2000)
  }

  if (isLoading) {
    return (
      <><Loading /></>
    )
  }

  return (
    <>
      <>
        {isDemo && (
          <Helmet>
            <meta
              name="keywords"
              content="creative tim, updivision, material, node.js json:api, html dashboard, node.js, react, api admin, pro react node.js, html css dashboard node.js, material dashboard node.js, node.js api, pro react material dashboard, material admin, pro react dashboard, pro react admin, web dashboard, bootstrap 5 dashboard node.js, bootstrap 5, css3 dashboard, bootstrap 5 admin node.js, material dashboard bootstrap 5 node.js, frontend, api dashboard, responsive bootstrap 5 dashboard, api, material dashboard, material node.js bootstrap 5 dashboard, json:api"
            />
            <meta
              name="description"
              content="Full stack app with hundreds of reusable UI components powered by MUI component library, React and Node.js API"
            />
            <meta
              itemProp="name"
              content="Material Dashboard 2 React Node.js by Creative Tim & UPDIVISION"
            />
            <meta
              itemProp="description"
              content="Full stack app with hundreds of reusable UI components powered by MUI component library, React and Node.js API"
            />
            <meta
              itemProp="image"
              content="https://s3.amazonaws.com/creativetim_bucket/products/689/original/react-material-dashboard-pro-nodejs.jpg?1664790326"
            />
            <meta name="twitter:card" content="product" />
            <meta name="twitter:site" content="@creativetim" />
            <meta
              name="twitter:title"
              content="Material Dashboard 2 React Node.js by Creative Tim & UPDIVISION"
            />
            <meta
              name="twitter:description"
              content="Full stack app with hundreds of reusable UI components powered by MUI component library, React and Node.js API"
            />
            <meta name="twitter:creator" content="@creativetim" />
            <meta
              name="twitter:image"
              content="https://s3.amazonaws.com/creativetim_bucket/products/689/original/react-material-dashboard-pro-nodejs.jpg?1664790326"
            />
            <meta property="fb:app_id" content="655968634437471" />
            <meta
              property="og:title"
              content="Material Dashboard 2 React Node.js by Creative Tim & UPDIVISION"
            />
            <meta property="og:type" content="article" />
            <meta
              property="og:url"
              content="https://www.creative-tim.com/live/react-material-dashboard-node.js-pro/"
            />
            <meta
              property="og:image"
              content="https://s3.amazonaws.com/creativetim_bucket/products/689/original/react-material-dashboard-pro-nodejs.jpg?1664790326"
            />
            <meta
              property="og:description"
              content="Full stack app with hundreds of reusable UI components powered by MUI component library, React and Node.js API"
            />
            <meta property="og:site_name" content="Creative Tim" />
          </Helmet>
        )}
        {direction === 'rtl' ? (
          <CacheProvider value={rtlCache}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
              <CssBaseline />
              {layout === 'dashboard' && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand={
                      (transparentSidenav && !darkMode) || whiteSidenav
                        ? brandDark
                        : brandWhite
                    }
                    brandName="Rivio"
                    routes={routesRef.current}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <Configurator />
                </>
              )}
              {layout === 'vr' && <Configurator />}
              <Routes>
                <Route path="/auth/login" element={<Login />} />
                <Route path="/s/setup/:code" element={<SetupLink />} />
                <Route path="/dashboard/:id" element={<Dashboard />} />
                <Route path="/not-found" element={<NotFound />} />
                <Route path="/device/public/:id" element={<PublicViewDevice />} />
                <Route path="/instructions/installation/:id" element={<HtmlTemplate />} />
                {/* Onboarding Routes - No Auth, No Sidenav */}
                <Route path="/onboard/*" element={
                  <OnboardingProvider>
                    <Routes>
                      {/* <Route path="claim/:claim" element={<LandingPage />} /> */}
                      <Route path="claim/:claim" element={<ClaimLandingRouter />} />
                      <Route path="claim/:claim/install" element={<InstallFlowPage />} />
                      <Route path="entity-review" element={<EntityReview />} />
                      <Route path="address_review" element={<AddressReview />} />
                      <Route path="contact_review" element={<ContactReview />} />
                      <Route path="user-review" element={<UserReview />} />
                      <Route path="subscription_preview" element={<SubscriptionPreview />} />
                      <Route path="billing" element={<BillingPage />} />
                      <Route path="final_attestation" element={<FinalAttestation />} />
                      <Route path="success" element={<SuccessPage />} />
                    </Routes>
                  </OnboardingProvider>
                } />
                {getRoutes(routesRef.current)}
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={
                        userDetails.role === 1
                          ? '/device-list'
                          : '/rivio'
                      }
                    />
                  }
                />
              </Routes>
            </ThemeProvider>
          </CacheProvider>
        ) : (
          <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            {layout === 'dashboard' && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={
                    (transparentSidenav && !darkMode) || whiteSidenav
                      ? brandDark
                      : brandWhite
                  }
                  brandName="Rivio"
                  routes={routesRef.current}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
              </>
            )}
            {layout === 'vr' && <Configurator />}
            <ToastContainer />
            <Routes>
              <Route path="/auth/login" element={<Login />} />
              <Route path="/device/public/:id" element={<PublicViewDevice />} />
              <Route path="/not-found" element={<NotFound />} />
              <Route path="/instructions/installation/:id" element={<HtmlTemplate />} />
              <Route path="/dashboard/:id" element={<Dashboard />} />
              {/* <Route path="/auth/register" element={<Register />} /> */}
              <Route
                path="/auth/forgot-password"
                element={<ForgotPassword />}
              />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* SMS account-setup short link: public, outside the auth guard */}
              <Route path="/s/setup/:code" element={<SetupLink />} />
              <Route path="/auth/verifyemail" element={<EmailVerification />} />
              <Route path="/auth/verifyemail/:token" element={<VerifyEmailPage />} />

              {/* Onboarding Routes - No Auth, No Sidenav */}
              <Route path="/onboard/*" element={
                <OnboardingProvider>
                  <Routes>
                    {/* <Route path="claim/:claim" element={<LandingPage />} /> */}
                    <Route path="claim/:claim" element={<ClaimLandingRouter />} />
                    <Route path="claim/:claim/install" element={<InstallFlowPage />} />
                    <Route path="entity-review" element={<EntityReview />} />
                    <Route path="address_review" element={<AddressReview />} />
                    <Route path="contact_review" element={<ContactReview />} />
                    <Route path="user-review" element={<UserReview />} />
                    <Route path="subscription_preview" element={<SubscriptionPreview />} />
                    <Route path="billing" element={<BillingPage />} />
                    <Route path="final_attestation" element={<FinalAttestation />} />
                    <Route path="success" element={<SuccessPage />} />
                    <Route path="continue/:token" element={<CustomerContinuation />} />
                  </Routes>
                </OnboardingProvider>
              } />

              <Route
                path="/service-request-detail/:id"
                element={<ServiceRequestDetail />}
              />
              <Route path="/rivio" element={<Summary />} />
              {getRoutes(routesRef.current)}
              {getCrudRoutes(crudRoutes)}
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      userDetails.role === 1
                        ? '/device-list'
                        : '/rivio'
                    }
                  />
                }
              />
            </Routes>
          </ThemeProvider>
        )}
        {authContext.loadingUI ? <Loading transparent={true} /> : ''}
      </>
    </>
  )
}
